import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import usersService from '../services/usersService';
import {User} from '../types/User';
import {DataTable, DataTableFilterMeta} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {useNavigate} from "react-router-dom";
import {handleError} from "../../../common/utils/errorHandling";
import {useAdminConfirmDialog} from "../../../common/hooks/useAdminConfirmDialog";
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {FilterMatchMode} from "primereact/api";
import {useToast} from "../../../common/contexts/ToastContext";

const UsersList: React.FC = () => {
  const {t} = useTranslation();
  const {showError} = useToast();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const [users, setUsers] = useState<User[]>([]);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    firstName: {value: null, matchMode: FilterMatchMode.CONTAINS},
    lastName: {value: null, matchMode: FilterMatchMode.CONTAINS},
    email: {value: null, matchMode: FilterMatchMode.CONTAINS}
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (): Promise<void> => {
    await handleError(
      async () => {
        const usersData = await usersService.getAllUsers();
        setUsers(usersData);
      },
      t('admin.users.errors.fetching')
    );
  };

  const handleEdit = (user: User) => {
    navigate(`${user.id}/edit`);
  };

  const handleDisable = async (user: User): Promise<void> => {
    showConfirmDialog({
      message: t('admin.common.messages.confirmDisableWithName', {name: `${user.firstName} ${user.lastName}`}),
      header: t('admin.common.actions.update'),
      acceptLabel: t('admin.common.actions.update'),
      accept: async () => {
        await handleError(
          async () => {
            await usersService.disableUser(user.id);
            await fetchUsers();
          },
          t('admin.users.errors.disabling')
        );
      }
    });
  };

  const confirmedBodyTemplate = (user: User) => {
    return !user.confirmed ? (
      <span className="px-3 py-1 bg-gray-400 text-white text-xs rounded-sm">{t('admin.users.status.unconfirmed')}</span>
    ) : null;
  };

  const disabledBodyTemplate = (user: User) => {
    return user.disabled ? (
      <span className="px-3 py-1 bg-gray-400 text-white text-xs rounded-sm">{t('admin.users.status.disabled')}</span>
    ) : null;
  };

  return (
    <>
      <AdminFormHeader
        title={t('admin.users.title.list')}
        subtitle={t('admin.users.subtitle.list')}
      />

      <DataTable
        value={users}
        stripedRows
        paginator
        rows={10}
        filterDisplay="row"
        filters={filters}
        columnResizeMode="fit"
        resizableColumns
        showGridlines
      >
        <Column
          sortField="firstName"
          header={t('admin.users.columns.firstName')}
          sortable
          filter
          filterField="firstName"
          filterPlaceholder={t('admin.users.filters.searchByFirstName')}
          showFilterMenu={false}
          showClearButton={false}
          body={(user: User) => (
            <strong>{user.firstName}</strong>
          )}
        />
        <Column
          sortField="lastName"
          header={t('admin.users.columns.lastName')}
          sortable
          filter
          filterField="lastName"
          filterPlaceholder={t('admin.users.filters.searchByLastName')}
          showFilterMenu={false}
          showClearButton={false}
          body={(user: User) => (
            <strong>{user.lastName}</strong>
          )}
        />
        <Column
          sortField="email"
          header={t('admin.users.columns.email')}
          sortable
          filter
          filterField="email"
          filterPlaceholder={t('admin.users.filters.searchByEmail')}
          showFilterMenu={false}
          showClearButton={false}
          body={(user: User) => (
            <span>{user.email}</span>
          )}
        />
        <Column
          sortField="role"
          header={t('admin.users.columns.role')}
          sortable
          body={(user: User) => (
            <span className="px-3 py-1 bg-gray-400 text-white text-xs rounded-sm">{user.role}</span>
          )}
        />
        <Column
          header={t('admin.users.columns.status')}
          body={(user: User) => (
            <div className="flex gap-1">
              {confirmedBodyTemplate(user)}
              {disabledBodyTemplate(user)}
            </div>
          )}
        />
        <Column
          header={t('admin.common.columns.actions')}
          body={(user: User) => (
            <div>
              <Button
                onClick={() => handleEdit(user)}
                icon="fa fa-edit"
                severity="info"
                size="small"
                outlined
                className="mr-2"
                tooltip={t('admin.common.actions.edit')}
                tooltipOptions={{position: 'top'}}
              />
              {!user.disabled && (
                <Button
                  onClick={() => handleDisable(user)}
                  icon="fa fa-ban"
                  severity="warning"
                  size="small"
                  outlined
                  tooltip={t('admin.common.actions.disable')}
                  tooltipOptions={{position: 'top'}}
                />
              )}
            </div>
          )}
        />
      </DataTable>
    </>
  );
};

export default UsersList;

